import {getToken, onMessage} from "firebase/messaging";
import {useEffect, useState} from "react";
import {
  firebaseVapidKey,
  messaging,
  messagingServiceWorkerRegistration,
} from "../utils/firebase";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {notificationApi} from "../services/notificationService";
import {interventionDate} from "../utils/date";
import {useDispatch} from "react-redux";
import {useSelector} from "react-redux";
import {addNotif, addOffer} from "../store/actions/notifActions";
import icon from "../images/mini-logo.png";
import {LocalUrl} from "../services/AxiosInstance";
import {authApi, WEB_PUSH_TOKEN_KEY} from "../services/AuthService";
import {socket} from "../socket/socket";
import {
  claimNotification,
  notificationFingerprint,
} from "../utils/notificationDedup";
import {getNotificationInterventionIdentifier} from "../utils/interventionUrl";

export const useNotificationPermission = ({userId}) => {
  useEffect(() => {
    if (!userId) return;

    const requestPermission = async () => {
      try {
        if (!("Notification" in window)) return;

        const permission = await Notification.requestPermission();
        if (permission === "granted") {
          const serviceWorkerRegistration =
            await messagingServiceWorkerRegistration;
          if (!serviceWorkerRegistration) return;

          const token = await getToken(messaging, {
            vapidKey: firebaseVapidKey,
            serviceWorkerRegistration,
          });
          if (token) {
            localStorage.setItem(WEB_PUSH_TOKEN_KEY, token);
            await authApi.addFcmToken({body: {fcmToken: token}});

            // Send the token to your server for further use
          } else {
            // No registration token available
          }
        } else {
          // Notification permission not granted
        }
      } catch (error) {
        // Error requesting notification permission
      }
    };

    requestPermission();
  }, [userId]);
};

export const useNotification = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [notificationList, setNotificationList] = useState([]);
  const selectedRole = useSelector((state) => state.auth.auth?.selectedRole);

  useEffect(() => {
    const invalidateRealtimeData = () => {
      queryClient.invalidateQueries({queryKey: ["offers"]});
      queryClient.invalidateQueries({queryKey: ["offer"]});
      queryClient.invalidateQueries({queryKey: ["chatRooms"]});
    };

    const handleOfferUpdate = (data) => {
      if (data?.status === "PUBLIC" && selectedRole === "TECHNICIAN") {
        dispatch(addOffer(true));
        return;
      }
      invalidateRealtimeData();
    };

    const handleSocketNotification = (data) => {
      if (
        data?.notifiableType === "offer" ||
        data?.data?.notifiableType === "offer"
      ) {
        dispatch(addOffer(true));
      }
      queryClient.invalidateQueries({queryKey: ["notifications"]});
    };

    const token = localStorage.getItem("token");
    if (token) {
      socket.auth = {authorization: "Bearer " + token};
      if (!socket.connected) socket.connect();
    }

    const offerEvents = ["offer_updated"];
    offerEvents.forEach((eventName) =>
      socket.on(eventName, handleOfferUpdate),
    );
    socket.on("receive_notif", handleSocketNotification);

    const unsubscribe = onMessage(messaging, (payload) => {
      const fingerprint = notificationFingerprint(payload);
      if (!claimNotification(fingerprint)) return;
      const title = payload.notification?.title || "Help Clic";
      const body = payload.notification?.body || "";
      let data = {};
      try {
        data =
          typeof payload.data?.data === "string"
            ? JSON.parse(payload.data.data)
            : payload.data?.data || payload.data || {};
      } catch {
        data = payload.data || {};
      }

      let path = LocalUrl;
      if (data?.notifiableType === "offer") {
        path += "/annonce/" + data.notifiableId;
      } else if (data?.notifiableType === "intervention") {
        path += "/intervention/" + getNotificationInterventionIdentifier(data);
      } else if (["ticket", "ticketMessage"].includes(data?.notifiableType)) {
        path += "/reclamation/" + (data.ticketId || data.notifiableId);
      } else if (data?.chatroomId) {
        path += "/messages#" + data.chatroomId;
      } else if (data?.notifiableType === "proposition") {
        path += "/intervention/" + getNotificationInterventionIdentifier(data);
      }

      if (Notification.permission === "granted") {
        const notification = new Notification(title, {
          body,
          icon: icon || "/icons/notification-icon.png",
          tag: fingerprint,
          renotify: false,
        });
        notification.onclick = (event) => {
          event.preventDefault();
          window.focus();
          window.location.href = path;
        };
      }

      const today = new Date();
      dispatch(addNotif({...payload, date: interventionDate(today)}));
      const isOfferNotification = data?.notifiableType === "offer";
      if (isOfferNotification && selectedRole === "TECHNICIAN") {
        dispatch(addOffer(true));
      } else {
        invalidateRealtimeData();
      }
      queryClient.invalidateQueries({queryKey: ["notifications"]});
      queryClient.invalidateQueries({queryKey: ["ticket"]});
      setNotificationList((prev) => [
        ...prev,
        {...payload, date: interventionDate(today)},
      ]);
    });

    return () => {
      unsubscribe();
      offerEvents.forEach((eventName) =>
        socket.off(eventName, handleOfferUpdate),
      );
      socket.off("receive_notif", handleSocketNotification);
    };
  }, [dispatch, queryClient, selectedRole]);

  return {notificationList};
};

export const useNotifications = ({page, size}) => {
  const {data, isLoading, isError, error, refetch} = useQuery({
    queryKey: ["notifications", page, size],
    queryFn: () => notificationApi.getMe({page, size}),
    select: (response) => {
      return response.rows;
    },
  });

  return {
    data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
export const useMarkAsRead = () => {
  const queryClient = useQueryClient();
  const {mutate} = useMutation({
    mutationFn: notificationApi.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ["notifications"]});
    },
  });

  const onMarkAsRead = () => {
    mutate();
  };
  return {
    onMarkAsRead,
  };
};
